import { useEffect, useMemo, useState, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import Loading from '../../common/Loading';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import useMultiOpeningMatStore from '../../../stores/useMultiOpeningMatStore';
import PhotoNavigation from './PhotoNavigation';
import StatusBar from './StatusBar';
import PhotoPagination from './PhotoPagination';
import PhotoThumbnails from './PhotoThumbnails';
import PhotoCropper from './PhotoCropper';
import { getEmptyStateMessage } from '../../../lib/utils';
import MultiOpeningMatVisualization from '../MultiOpening/MultiOpeningMatVisualization';
import FramingPreview from '../FramedPrints/FramingPreview';
import {buildFramingConfig,SUPPORTED_FRAMING_PREVIEW_FRAME_TYPES} from '../../../lib/framingConfig';
import framingStyles from '../FramedPrints/FramingPreview.module.css';
import useFramingPreviewImageStore from '../../../stores/useFramingPreviewImageStore';

const PhotoDisplay = ({ lineItemId, isLoading }) => {

  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [cropEdit, setCropEdit] = useState(null);
  const [editedCropLineItem, setEditedCropLineItem] = useState(null);
  const cropperRef = useRef(null);
  const restoredCropLineItemRef = useRef(null);
  const restoredOrientationLineItemRef = useRef(null);
  const { addToCart, photos, selectedPhoto, selectAllPhotos, photosLoadingMessage, form, setForm, productType, finishingServicesData, lineItem } = useProductSelectionStore(
    useShallow((state) => ({
      addToCart: state.addToCart,
      photos: state.photos,
      selectedPhoto: state.selectedPhoto,
      selectAllPhotos: state.selectAllPhotos,
      photosLoadingMessage: state.photosLoadingMessage,
      form: state.form,
      setForm: state.setForm,
      productType: state.productType,
      finishingServicesData: state.finishingServicesData,
      lineItem: state.lineItem,
    })),
  );
  const loadPreviewImage = useFramingPreviewImageStore((state) => state.loadPreviewImage);
  const multiOpeningSelectedProduct = useMultiOpeningMatStore((state) => state.selectedProduct);
  const frameType = form.inputs.frameType?.selectedValueText;

  const supportsFramingPreview = ['framed-prints', 'canvas'].includes(productType) && SUPPORTED_FRAMING_PREVIEW_FRAME_TYPES.includes(frameType);
  const savedFinishingCategory = lineItem?.attributes?.finishingService?.finishingCategory || '';
  const isEditingFramedItem = Boolean(
    lineItemId &&
    ['framed-prints', 'canvas'].includes(productType) &&
    /fram/i.test(savedFinishingCategory)
  );
  const lineItemKey = lineItem?.id || lineItem?.attributes?.guid;

  const savedCroppedPhotoUrl = lineItemId ? lineItem?.attributes?.photographCroppedUrl || lineItem?.attributes?.photographUrl : null;
  const savedCroppedPhoto = useFramingPreviewImageStore((state) => savedCroppedPhotoUrl ? state.previewImages[savedCroppedPhotoUrl] : null);

  const useSavedCroppedPhoto = savedCroppedPhotoUrl && editedCropLineItem !== lineItemKey;
  const currentWidth = Number.parseFloat(form?.meta?.xAspectRatio);
  const currentHeight = Number.parseFloat(form?.meta?.yAspectRatio);

  const savedCropIsLandscape = savedCroppedPhoto?.status === 'ready' && savedCroppedPhoto.width > savedCroppedPhoto.height;
  const savedCropIsPortrait = savedCroppedPhoto?.status === 'ready' && savedCroppedPhoto.height > savedCroppedPhoto.width;

  const shouldSwapSavedOrientation =
    restoredOrientationLineItemRef.current !== lineItemKey &&
    currentWidth > 0 &&
    currentHeight > 0 &&
    currentWidth !== currentHeight &&
    ((savedCropIsLandscape && currentWidth < currentHeight) ||
      (savedCropIsPortrait && currentWidth > currentHeight));
  const previewForm = shouldSwapSavedOrientation
    ? {
        ...form,
        meta: {
          ...form.meta,
          xAspectRatio: form.meta.yAspectRatio,
          yAspectRatio: form.meta.xAspectRatio,
          aspectRatio: form.meta.yAspectRatio / form.meta.xAspectRatio,
        },
      }
    : form;
  const baseFramingConfig = useMemo(
    () => supportsFramingPreview
      ? buildFramingConfig({
          form: previewForm,
          finishingServicesData,
          selectedPhoto,
          selectAllPhotos,
        })
      : null,
    [
      supportsFramingPreview,
      previewForm,
      finishingServicesData,
      selectedPhoto,
      selectAllPhotos,
    ],
  );
  const framingConfig = useMemo(
    () => useSavedCroppedPhoto && baseFramingConfig?.status === 'ready'
      ? {
          ...baseFramingConfig,
          artwork: {
            ...baseFramingConfig.artwork,
            url: savedCroppedPhotoUrl,
            preCropped: true,
          },
        }
      : baseFramingConfig,
    [useSavedCroppedPhoto, baseFramingConfig, savedCroppedPhotoUrl],
  );

  const showFramePreview = framingConfig?.status === 'ready' && !cropEdit;
  const frameTextureUrl = framingConfig?.status === 'ready' ? framingConfig.frame?.assetUrl : null;
  const previewArtworkUrl = framingConfig?.status === 'ready' ? framingConfig.artwork?.url : null;
  const frameTextureStatus = useFramingPreviewImageStore((state) =>frameTextureUrl ? state.previewImages[frameTextureUrl]?.status : null);
  const previewArtworkStatus = useFramingPreviewImageStore((state) => previewArtworkUrl ? state.previewImages[previewArtworkUrl]?.status : null);
  const isEditFramePreviewLoading = isEditingFramedItem && (
    !supportsFramingPreview ||
    framingConfig?.status === 'waiting' ||
    (framingConfig?.status === 'ready' && (
      !['ready', 'error'].includes(frameTextureStatus) ||
      !['ready', 'error'].includes(previewArtworkStatus) ||
      shouldSwapSavedOrientation
    ))
  );
  const isNewFramePreviewLoading = Boolean(
    !lineItemId &&
    framingConfig?.status === 'ready' &&
    (
      (frameTextureUrl && !['ready', 'error'].includes(frameTextureStatus)) ||
      (previewArtworkUrl && !['ready', 'error'].includes(previewArtworkStatus))
    )
  );

  useEffect(() => {
    loadPreviewImage(savedCroppedPhotoUrl);
  }, [savedCroppedPhotoUrl, loadPreviewImage]);

  useEffect(() => {
    loadPreviewImage(frameTextureUrl);
    loadPreviewImage(previewArtworkUrl);
  }, [frameTextureUrl, previewArtworkUrl, loadPreviewImage]);

  useEffect(() => {
    setCropEdit(null);
  }, [selectedPhoto?.id, productType, frameType, selectAllPhotos]);

  useEffect(() => {
    if (!supportsFramingPreview || !lineItem) return;
    if (restoredCropLineItemRef.current === lineItemKey) return;

    const crop = lineItem.attributes?.photographAttributes;
    if (!crop) return;
    const cropPositionX = Number.parseFloat(crop.cropX ?? crop.cropx);
    const cropPositionY = Number.parseFloat(crop.cropY ?? crop.cropy);
    const cropWidth = Number.parseFloat(crop.cropW);
    const cropHeight = Number.parseFloat(crop.cropH);
    if (![cropPositionX, cropPositionY, cropWidth, cropHeight].every(Number.isFinite)) return;

    restoredCropLineItemRef.current = lineItemKey;
    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        cropPositionX,
        cropPositionY,
        cropWidth,
        cropHeight,
      },
    }));
  }, [supportsFramingPreview, lineItem, setForm]);

  useEffect(() => {
    if (
      !supportsFramingPreview ||
      !lineItemKey ||
      savedCroppedPhoto?.status !== 'ready' ||
      restoredOrientationLineItemRef.current === lineItemKey
    ) return;

    restoredOrientationLineItemRef.current = lineItemKey;

    if (!shouldSwapSavedOrientation) return;

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        xAspectRatio: prevState.meta.yAspectRatio,
        yAspectRatio: prevState.meta.xAspectRatio,
        aspectRatio: prevState.meta.yAspectRatio / prevState.meta.xAspectRatio,
      },
    }));
  }, [
    supportsFramingPreview,
    lineItemKey,
    savedCroppedPhoto?.status,
    shouldSwapSavedOrientation,
    setForm,
  ]);

  // Calculate aspect ratio from x/y values
  useEffect(() => {
    if (form.meta.yAspectRatio === undefined) return

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        aspectRatio: prevState.meta.xAspectRatio / prevState.meta.yAspectRatio,
      },
    }))
  }, [form?.meta?.xAspectRatio, form?.meta?.yAspectRatio])

  if (addToCart) {
    return <Loading message={lineItemId ? "Updating Order" : "Adding to cart..."} />;
  }

  if (isEditFramePreviewLoading || isNewFramePreviewLoading) {
    return <Loading message="Loading frame preview..." />;
  }

  const handleCropBoxRotate = () => {
    const cropper = cropperRef.current?.cropper
    const hasAspectRatio = form?.meta?.aspectRatio !== undefined
    if (!cropper && !(showFramePreview && hasAspectRatio)) return

    if (hasAspectRatio) {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          xAspectRatio: prevState.meta.yAspectRatio,
          yAspectRatio: prevState.meta.xAspectRatio,
        },
      }))
    } else {
      cropper.rotate(90)
      requestAnimationFrame(() => {
        const containerData = cropper.getContainerData()
        const canvasData = cropper.getCanvasData()
        
        if (!containerData || !canvasData) return
        
        // Center the canvas horizontally and position at top
        const left = (containerData.width - canvasData.width) / 2
        const top = 45
        
        cropper.setCanvasData({
          left: left,
          top: top,
        })
      })
    }
  }

  const onCrop = (event) => {
    const cropper = cropperRef.current?.cropper
    const cropData = cropper?.getData()
    const imageData = cropper?.getImageData()
    const originalWidth = imageData?.naturalWidth
    const originalHeight = imageData?.naturalHeight
    if (!cropData || !originalWidth || !originalHeight) return
    const cropXPercent = ((cropData.x < 0 ? 0 : cropData.x) / originalWidth) * 100
    const cropYPercent = ((cropData.y < 0 ? 0 : cropData.y) / originalHeight) * 100
    const cropWPercent = (cropData.width / originalWidth) * 100
    const cropHPercent = (cropData.height / originalHeight) * 100

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...prevState.meta,
        cropPositionX: cropXPercent,
        cropPositionY: cropYPercent,
        cropWidth: cropWPercent,
        cropHeight: cropHPercent,
      },
    }))
  }

  return (
    <div
      className={`product-selection-picture-container ${lineItemId ? styles.editModeContainer : ''} ${lineItemId && (showFramePreview || multiOpeningSelectedProduct) ? styles.editFramePreviewContainer : ''} ${lineItemId && (cropEdit || form?.meta?.matOpeningShape === 'oval' || (isEditingFramedItem && !showFramePreview)) ? styles.editCropModeContainer : ''}`}
      onMouseEnter={() => setIsHoveringImage(true)}
      onMouseLeave={() => setIsHoveringImage(false)}
    >
      {!multiOpeningSelectedProduct && !isLoading && <PhotoNavigation isHoveringImage={isHoveringImage} />}

      {/* <StatusBar onSaveChanges={onSaveChanges} /> */}
      {(photosLoadingMessage || isLoading) && (
          <Loading message={photosLoadingMessage || 'Loading...'} />
        )}

      <div className={`product-selection-main-picture-container ${styles.mainPictureContainerScrollable} ${productType === 'framed-prints' || supportsFramingPreview ? framingStyles.layout : ''}`}>
      {photosLoadingMessage && (
          <Loading message={photosLoadingMessage} />  
        )}
        {(productType === 'albums' || photos.length === 0) && !showFramePreview && !multiOpeningSelectedProduct && !photosLoadingMessage && !isLoading && (
          <div className={styles.emptyPhotosState}>
            <p>No photos yet.</p>
            <p>{getEmptyStateMessage(productType)}</p>
          </div>
        )}

        {productType === 'multi-opening' && multiOpeningSelectedProduct && !photosLoadingMessage && !isLoading && (
          <MultiOpeningMatVisualization />
        )}

        {showFramePreview && !photosLoadingMessage && !isLoading && (
          <div className={framingStyles.mainDisplay}>
            <FramingPreview config={framingConfig} imagesPreloaded={isEditingFramedItem} />
            {selectedPhoto?.attributes && !selectAllPhotos && (
              <button
                type="button"
                className={framingStyles.editCropButton}
                aria-label="Edit Crop"
                title="Edit Crop"
                onClick={() => {
                  setEditedCropLineItem(lineItemKey);
                  setCropEdit({ initialCrop: framingConfig.artwork.crop });
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M6 3v15h15M3 6h15v15" />
                </svg>
              </button>
            )}
          </div>
        )}
       
        {!showFramePreview && (productType !== 'multi-opening' || !multiOpeningSelectedProduct) && productType !== 'albums' && photos.length > 0 && !photosLoadingMessage && !isLoading && selectedPhoto?.attributes && (
          <div className="main-picture-grid">
            {cropEdit && framingConfig?.status === 'ready' && (
              <button
                type="button"
                className={framingStyles.backButton}
                onClick={() => setCropEdit(null)}
              >
                Back to Preview
              </button>
            )}
            <div
              className={`slider-mask w-slider-mask ${styles.sliderMaskContainer}`}
              id="w-slider-mask-0"
            >
              {photos.map((photo, index) => {
                const currentIndex = photos.findIndex(p => p.id === selectedPhoto?.id);
                const isActive = selectedPhoto?.id === photo.id;

                return (
                  <div
                    key={photo.id}
                    className={`slide-2 slide-one w-slide ${styles.slideItem} ${isActive ? styles.slideItemFirst : styles.slideItemAbsolute}`}
                    aria-label={`${index + 1} of ${photos.length}`}
                    role="group"
                    style={{
                      transform: `translateX(${(index - currentIndex) * 100}%)`,
                    }}
                    aria-hidden={!isActive}
                  >
                    <div className="div-block-345" aria-hidden={!isActive}>
                      {isActive && !selectAllPhotos ? (
                        <PhotoCropper
                          photo={photo}
                          cropperRef={cropperRef}
                          onCrop={onCrop}
                          aspectRatioOverride={cropEdit ? form.meta.aspectRatio : undefined}
                          initialCrop={cropEdit?.initialCrop}
                        />
                      ) : (
                        <img
                          src={photo?.attributes?.presignedPhotoUrl}
                          loading="lazy"
                          alt={photo?.attributes?.photoFileName}
                          className={`image-91 ${styles.placeholderImage}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              <div
                aria-live="off"
                aria-atomic="true"
                className={`w-slider-aria-label ${styles.srOnly}`}
                data-wf-ignore=""
              >
                Slide {photos.findIndex(p => p.id === selectedPhoto?.id) + 1} of {photos.length}.
              </div>
            </div>
          </div>
        )}
      </div>

      {productType !== 'albums' && !multiOpeningSelectedProduct && photos.length > 0 && !isLoading && !showFramePreview && (
        <PhotoPagination onRotate={handleCropBoxRotate} />
      )}

      {productType !== 'albums' && photos.length > 0 && !lineItemId && !isLoading && (
        <PhotoThumbnails />
      )}
    </div>
  );
};

export default PhotoDisplay;
